if (!customElements.get('candle-recipe-calculator')) {
  customElements.define(
    'candle-recipe-calculator',
    class CandleRecipeCalculator extends HTMLElement {
      constructor() {
        super();

        this.animationFrames = {};
        this.numberFormatter = new Intl.NumberFormat(document.documentElement.lang || 'pl-PL', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        this.fieldLimits = {
          weight: { min: 0.01, max: 10000, integer: false },
          fragrance: { min: 0, max: 100, integer: false },
          quantity: { min: 1, max: 10000, integer: true },
          dye: { min: 0, max: 100, integer: false },
        };
      }

      connectedCallback() {
        this.inputs = {
          weight: this.querySelector('[data-input="weight"]'),
          fragrance: this.querySelector('[data-input="fragrance"]'),
          quantity: this.querySelector('[data-input="quantity"]'),
          dye: this.querySelector('[data-input="dye"]'),
        };
        this.results = {
          wax: this.querySelector('[data-result="wax"]'),
          fragrance: this.querySelector('[data-result="fragrance"]'),
          dye: this.querySelector('[data-result="dye"]'),
        };
        this.errors = {
          weight: this.querySelector('[data-error="weight"]'),
          fragrance: this.querySelector('[data-error="fragrance"]'),
          quantity: this.querySelector('[data-error="quantity"]'),
          dye: this.querySelector('[data-error="dye"]'),
        };

        const onInput = FoxTheme.utils.debounce(this.calculate.bind(this), 150);
        Object.values(this.inputs).forEach((input) => {
          if (input) {
            input.addEventListener('input', onInput);
          }
        });
      }

      formatNumber(value) {
        // Intl joins every digit group with U+00A0 (non-breaking space), so a large result
        // becomes one unbreakable token the browser can only overflow, never wrap - swap it
        // for a normal breakable space so the number can wrap at its digit-group boundaries
        // instead of spilling out of the fixed-width result tile.
        return this.numberFormatter.format(value).replace(/ /g, ' ');
      }

      validateField(key) {
        const input = this.inputs[key];
        const limits = this.fieldLimits[key];

        if (!input) {
          return { value: 0, valid: true };
        }

        const raw = input.value.trim();
        if (raw === '') {
          this.setFieldError(key, false);
          return { value: 0, valid: true };
        }

        const value = Number(raw.replace(',', '.'));
        const isNumber = Number.isFinite(value);
        const isInteger = !limits.integer || Number.isInteger(value);
        const inRange = isNumber && value >= limits.min && value <= limits.max;
        const valid = isNumber && isInteger && inRange;

        this.setFieldError(key, !valid);
        return { value: valid ? value : 0, valid };
      }

      setFieldError(key, hasError) {
        const input = this.inputs[key];
        const errorElement = this.errors[key];

        if (input) {
          input.setAttribute('aria-invalid', hasError ? 'true' : 'false');
        }
        if (errorElement) {
          errorElement.classList.toggle('hidden', !hasError);
        }
      }

      resetResults() {
        Object.keys(this.results).forEach((key) => {
          cancelAnimationFrame(this.animationFrames[key]);
          if (this.results[key]) {
            this.results[key].textContent = this.formatNumber(0);
          }
        });
      }

      calculate() {
        const weight = this.validateField('weight');
        const fragrance = this.validateField('fragrance');
        const quantity = this.validateField('quantity');
        const dye = this.validateField('dye');

        if (!weight.valid || !fragrance.valid || !quantity.valid || !dye.valid) {
          this.resetResults();
          return;
        }

        const fragrancePerCandle = (weight.value * fragrance.value) / 100;
        const dyePerCandle = (weight.value * dye.value) / 100;
        const waxPerCandle = Math.max(0, weight.value - fragrancePerCandle - dyePerCandle);

        this.animateTo('wax', waxPerCandle * quantity.value);
        this.animateTo('fragrance', fragrancePerCandle * quantity.value);
        this.animateTo('dye', dyePerCandle * quantity.value);
      }

      animateTo(key, target) {
        const element = this.results[key];
        if (!element) {
          return;
        }

        cancelAnimationFrame(this.animationFrames[key]);

        const duration = 600;
        const startTime = performance.now();
        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

        const step = (now) => {
          const progress = Math.min((now - startTime) / duration, 1);
          const value = target * easeOutCubic(progress);
          element.textContent = this.formatNumber(value);

          if (progress < 1) {
            this.animationFrames[key] = requestAnimationFrame(step);
          }
        };

        this.animationFrames[key] = requestAnimationFrame(step);
      }
    }
  );
}
